// Capa HTTP del dominio "usuarios" (mantenedor de usuarios, Módulo B).
// Tipos + funciones fetch. El estado de la lista vive en TanStack Query
// (ver `src/hooks/useUsuarios.ts`), no aquí — mismo patrón que `lib/auth.ts`.

import { apiFetch } from "./api";

export type EstadoUsuario = "activo" | "invitacion_pendiente";

/** Recorte del perfil que viaja dentro del usuario (`whenLoaded` en el API). */
export interface PerfilDeUsuario {
  id: number;
  nombre: string;
  codigo: string | null;
}

export interface Usuario {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  /**
   * Forma canónica del backend: `12345678-9` (ver `lib/rut.ts`).
   *
   * NULLABLE, y el tipo tiene que decirlo. La migración los dejó nullable en
   * base y obligatorios en la aplicación —divergencia declarada del diseño—
   * SIN backfill, así que toda fila anterior a esa migración llega con null.
   * Declararlos `string` fue una mentira que TypeScript no podía atrapar,
   * porque la respuesta del API se castea y no se valida: el listado reventaba
   * con "can't access property replace, rut is null".
   */
  rut: string | null;
  cargo: string | null;
  /**
   * OPCIONAL de verdad, a diferencia de `rut` y `cargo`: aquellos son nullable
   * solo porque la migración no pudo rellenarlos, y el request los exige. Este
   * puede quedar vacío para siempre y está bien.
   *
   * Es el número que el CLIENTE ve en el portal y en los dos correos, así que
   * `null` no es un dato faltante: es la decisión de no publicarlo, y esas
   * pantallas omiten la línea.
   */
  telefono: string | null;
  /** Conmutador de acceso (RF-B1). `false` bloquea el próximo request. */
  acceso: boolean;
  /**
   * `whenLoaded('perfil')`: el listado y la edición lo traen; el alta y el
   * PATCH de acceso NO cargan la relación, así que la clave viene ausente.
   * Por eso es opcional y por eso esas dos respuestas invalidan la lista en
   * vez de parchearla.
   */
  perfil?: PerfilDeUsuario;
  status: EstadoUsuario;
  invited_at: string | null;
  created_at: string;
}

export interface CrearUsuarioInput {
  first_name: string;
  last_name: string;
  email: string;
  email_confirmation: string;
  rut: string;
  cargo: string;
  /** Opcional. `null` cuando no se cargó — el backend nunca guarda `""`. */
  telefono: string | null;
  /**
   * Obligatorio: el alta elige el perfil, no lo hereda. El backend lo valida
   * contra el maestro y lo asigna por propiedad (nunca por mass assignment,
   * donde `User::$fillable` lo descartaría en silencio).
   */
  perfil_id: number;
}

/**
 * Los SIETE campos editables de la ficha (RF-B3, más el teléfono opcional).
 * `perfil_id` viaja acá pero el backend lo asigna por propiedad, nunca por mass
 * assignment.
 *
 * `telefono` va SIEMPRE, incluso vacío: el backend deja la fila intacta cuando
 * la clave no viene —para que un cliente desactualizado no le borre el número
 * al ejecutivo— así que omitirlo desde acá haría imposible vaciarlo.
 */
export interface ActualizarUsuarioInput {
  first_name: string;
  last_name: string;
  email: string;
  rut: string;
  cargo: string;
  telefono: string | null;
  perfil_id: number;
}

export async function fetchUsuarios(): Promise<Usuario[]> {
  const { data } = await apiFetch<{ data: Usuario[] }>("/api/users");
  return data;
}

/** El usuario creado, más si el correo de invitación llegó a despacharse. */
export interface CrearUsuarioResultado {
  usuario: Usuario;
  emailEnviado: boolean;
}

export async function crearUsuarioRequest(
  input: CrearUsuarioInput,
): Promise<CrearUsuarioResultado> {
  // El alta responde 201 aunque el envío del correo falle: el usuario queda
  // creado y se recupera con "reenviar invitación". Por eso hay que mirar el
  // flag y NO cantar "invitación enviada" cuando no salió nada.
  const { data, invitation_email_sent } = await apiFetch<{
    data: Usuario;
    invitation_email_sent: boolean;
  }>("/api/users", {
    method: "POST",
    body: JSON.stringify(input),
  });

  return { usuario: data, emailEnviado: invitation_email_sent };
}

export async function actualizarUsuarioRequest(
  id: number,
  input: ActualizarUsuarioInput,
): Promise<Usuario> {
  const { data } = await apiFetch<{ data: Usuario }>(`/api/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return data;
}

/**
 * Conmutador de acceso (RF-B1): endpoint propio y PATCH, separado de la ficha.
 * NO existe `DELETE /api/users/{user}`: los usuarios no se eliminan nunca, solo
 * se desactivan (hay un test en el backend que fija esa ausencia).
 */
export async function cambiarAccesoRequest(id: number, acceso: boolean): Promise<Usuario> {
  const { data } = await apiFetch<{ data: Usuario }>(`/api/users/${id}/acceso`, {
    method: "PATCH",
    body: JSON.stringify({ acceso }),
  });
  return data;
}

export function reenviarInvitacionRequest(id: number): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/api/users/${id}/resend-invitation`, {
    method: "POST",
  });
}
